import type { ViewerResponse } from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { ErrorState, LoadingState } from '../components/StateMessage';
import { AttributeLibrarySection } from '../components/admin/AttributeLibrarySection';
import { CapabilityLibrarySection } from '../components/admin/CapabilityLibrarySection';
import { RoleLibrarySection } from '../components/admin/RoleLibrarySection';
import { DepartmentsSection, OrganisationSection, TeamsSection } from '../components/admin/StructureSection';
import {
  EXPERIENCE_AREA_LIBRARY_CONFIG,
  QUALIFICATION_LIBRARY_CONFIG,
  SimpleLibrarySection,
  SKILL_LIBRARY_CONFIG,
} from '../components/admin/SimpleLibrarySection';
import { DocumentTypeLibrarySection } from '../components/admin/DocumentTypeLibrarySection';
import { EmployeeIdentitySection } from '../components/admin/EmployeeIdentitySection';
import { AuditSection } from '../components/admin/AuditSection';
import { EmployeeCreationSection } from '../components/admin/EmployeeCreationSection';
import { EmployeePlacementSection } from '../components/admin/EmployeePlacementSection';
import { ResponsibilitySection } from '../components/admin/ResponsibilitySection';
import styles from '../components/admin/admin.module.css';

/**
 * Organisation — the admin area (Mission 018).
 *
 * `docs/MVP_SCOPE.md` §7 criterion 6: "an admin can define/edit attributes,
 * roles and org structure without code changes", and
 * `docs/UX_ARCHITECTURE.md` §2's "Organisation *(admin area, not primary
 * nav)* — Org/department/team structure management, attribute/role library
 * management".
 *
 * ---------------------------------------------------------------------
 * THE VOCABULARY THIS PAGE KEEPS STRAIGHT
 *
 * Create, edit, retire and deprecate are four different acts. Each has its
 * own control and its own confirmation, and every confirmation says what
 * the act does *not* do — because the likeliest misunderstanding in an
 * admin area is that retiring something removes it.
 *
 * **Nothing here deletes anything, and no control offers to.** Every
 * definition table forbids deletion at the database level, and every
 * foreign key to one is `ON DELETE RESTRICT`. A "delete" button would be a
 * claim the system cannot honour.
 *
 * The one rule that refuses an edit outright lives in the attribute
 * library: an attribute's scale wording freezes once anybody has been rated
 * against it, because changing what 1 and 20 mean would silently restate
 * every rating already recorded (`docs/DOMAIN_MODEL.md` §2.5). The form
 * disables those fields and explains why, rather than letting somebody type
 * a new meaning and discover the rule on submit.
 *
 * WHY EACH SECTION LOADS ITSELF
 *
 * Five independent reads, the same discipline the profile and dashboard
 * follow: a failure in one section leaves the others usable, and after a
 * successful write a section re-reads the server rather than patching its
 * own copy. Nothing here is optimistic — an administrative change that
 * appeared to succeed and did not would be the worst failure this area
 * could have.
 * ---------------------------------------------------------------------
 */
export function OrganisationPage() {
  const viewer = useApiResource<ViewerResponse>('/me');

  if (viewer.status === 'loading') return <LoadingState label="Loading…" />;
  if (viewer.status === 'failed') {
    return <ErrorState title="This page could not be loaded" detail={describeError(viewer.error)} />;
  }

  // The server decides; this only decides what to show. Every route behind
  // this page checks authorization itself, so a non-administrator who
  // navigates here directly gets refusals rather than a working screen.
  if (!viewer.data.isAdministrator) {
    return (
      <ErrorState
        title="Administrators only"
        detail="The organisation admin area is where the attribute library, the role library and the organisation's structure are defined. Your account does not have administrator access."
      />
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Organisation</h1>
      </header>
      <p className={styles.lede}>
        The definitions everybody in this product is described and measured against. Nothing here is ever deleted:
        entries are retired, and the records that reference them stay readable.
      </p>

      <OrganisationSection />
      <AttributeLibrarySection />
      <SimpleLibrarySection {...SKILL_LIBRARY_CONFIG} />
      <SimpleLibrarySection {...QUALIFICATION_LIBRARY_CONFIG} />
      <SimpleLibrarySection {...EXPERIENCE_AREA_LIBRARY_CONFIG} />
      <CapabilityLibrarySection />
      <DocumentTypeLibrarySection />
      <RoleLibrarySection />
      <DepartmentsSection />
      <TeamsSection />
      <EmployeeCreationSection />
      <EmployeePlacementSection />
      <ResponsibilitySection />
      <EmployeeIdentitySection />
      <AuditSection />
    </div>
  );
}
